// Shared utils - pure utility functions

import { DestroyRef, Signal, inject, signal } from '@angular/core';

export * from './lib/account-number-mask';

/**
 * Crea un signal reactivo al resultado de una media query (`window.matchMedia`).
 *
 * SSR-safe: si no existe `window`/`matchMedia` devuelve un signal en `false`.
 * Debe invocarse en un contexto de inyección (p. ej. inicializador de campo)
 * para registrar la limpieza del listener vía `DestroyRef`.
 */
export function viewportMatches(query: string): Signal<boolean> {
  const state = signal(false);

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return state.asReadonly();
  }

  const mediaQuery = window.matchMedia(query);
  state.set(mediaQuery.matches);

  const listener = (event: MediaQueryListEvent): void => state.set(event.matches);
  mediaQuery.addEventListener('change', listener);

  inject(DestroyRef).onDestroy(() => mediaQuery.removeEventListener('change', listener));

  return state.asReadonly();
}

/**
 * Signal `true` cuando el viewport es menor al ancho indicado.
 * Densidad (grids, `step-progress`), no cromado.
 * Por defecto usa el breakpoint `md` (768px) de `_breakpoints.scss`.
 * No usar para decidir shell app vs escritorio: usar `isAppViewport()`.
 */
export function isMobileViewport(maxWidth = 768): Signal<boolean> {
  return viewportMatches(`(max-width: ${maxWidth - 0.02}px)`);
}

/** Alineado con `lg` en `_breakpoints.scss`. */
const APP_VIEWPORT_MAX_WIDTH = 1024;

/** Alineado con el default de `media-short` en `_breakpoints.scss`. */
const APP_VIEWPORT_SHORT_MAX_HEIGHT = 540;

/**
 * Signal `true` cuando toca cromado de app (no densidad).
 *
 * `true` si el ancho es menor a `lg` (1024px) o el viewport es landscape
 * corto (`media-short`: alto ≤ 540px). Cubre móvil en ambas orientaciones
 * y tableta P/M (768, 820). Escritorio desde `lg`.
 *
 * Debe invocarse en un contexto de inyección. No sustituye a
 * `isMobileViewport()`.
 */
export function isAppViewport(): Signal<boolean> {
  return viewportMatches(
    `(max-width: ${APP_VIEWPORT_MAX_WIDTH - 0.02}px), (max-height: ${APP_VIEWPORT_SHORT_MAX_HEIGHT}px) and (orientation: landscape)`,
  );
}
